import React, { useCallback, useMemo } from 'react';
import { ScrollView, View } from 'react-native';
import moment from 'moment';

import { PRODUCT_EXPIRY_DATE_POLICY } from '../../../common/constants';
import { valueExists } from '../../../common/utils/utils';
import {
	getProductExpiryDatePolicyName,
	isProductExpiryDateRequired,
	resolveProductExpiryDatePolicy,
} from '../../../common/utils/warehousing/productExpiryPolicy';
import { getLocalTime } from '../../../common/utils/time/time';

import { useThemedStyles } from '../../hooks/useThemedStyles';
import { Button, Dialog } from '../../components';
import { DateSelectorInput } from '../../components/input';
import TextInput from '../../components/input/TextInput';
import Text from '../../components/text/Text';
import StocktakeQuantitySelector from '../../components/stocktake/StocktakeQuantitySelector';

import { getExpirySummaryText } from './StockDeliveryDetailHelpers';

const getWarehouseProductForDeliveryItem = (item) => {
	return item?.destinationWarehouseProduct || item?.sourceWarehouseProduct || null;
};

const ReceiveWithExpiryDialog = ({
	item,
	rows,
	setRows,
	error,
	onClose,
	onSubmit,
	onConfirmQuantityChange,
	tenantSettings,
	timezone,
	sourceIsWarehouse,
	loading,
}) => {
	const styles = useThemedStyles(createStyles);
	const expectedQuantity = Math.max(0, Math.round(Number(item?.quantity) || 0));
	const rowsTotal = (rows || []).reduce((acc, row) => acc + Math.max(0, Math.round(Number(row.quantity) || 0)), 0);
	const canSubmit = rowsTotal === expectedQuantity && (expectedQuantity === 0 || rowsTotal > 0);
	const canConfirmQuantityChange = rowsTotal !== expectedQuantity && Boolean(onConfirmQuantityChange);
	const minimumExpiryDate = useMemo(() => {
		const minimumDateKey = getLocalTime(timezone).add(1, 'day').format('YYYY-MM-DD');
		return moment(minimumDateKey, 'YYYY-MM-DD', true).startOf('day').toDate();
	}, [timezone]);
	const expiryDatePolicy = useMemo(() => {
		if (item?.expiryPolicyContext?.policy) {
			return item.expiryPolicyContext.policy;
		}
		return resolveProductExpiryDatePolicy({
			product: item?.product,
			tenantSettings,
		});
	}, [item?.expiryPolicyContext?.policy, item?.product, tenantSettings]);
	const expiryDateRequired = useMemo(() => {
		if (item?.expiryPolicyContext?.policy) {
			return item.expiryPolicyContext.policy === PRODUCT_EXPIRY_DATE_POLICY.REQUIRED;
		}
		return isProductExpiryDateRequired({
			product: item?.product,
			tenantSettings,
		});
	}, [item?.expiryPolicyContext?.policy, item?.product, tenantSettings]);
	const expiryDateRecommended = expiryDatePolicy === PRODUCT_EXPIRY_DATE_POLICY.RECOMMENDED;

	const addRow = useCallback(() => {
		setRows(currentRows => ([
			...(currentRows || []),
			{ quantity: null, expiryDate: '', supplierBatchCode: '' },
		]));
	}, [setRows]);

	const removeRow = useCallback((index) => {
		setRows(currentRows => currentRows.filter((_, rowIndex) => rowIndex !== index));
	}, [setRows]);

	const updateRow = useCallback((index, field, value) => {
		setRows(currentRows => currentRows.map((row, rowIndex) => {
			if (rowIndex !== index) {
				return row;
			}
			return {
				...row,
				[field]: value,
			};
		}));
	}, [setRows]);

	return (
		<Dialog
			isOpen={Boolean(item)}
			onClose={onClose}
			shouldShowCloseIcon={true}
			enableCloseDialog={!loading}
		>
			{item && (
				<View style={styles.batchDialogContainer}>
					<ScrollView
						style={styles.batchDialogScroll}
						contentContainerStyle={styles.batchDialogScrollContent}
						keyboardShouldPersistTaps="handled"
					>
						<Text style={styles.dialogTitle}>
							{sourceIsWarehouse ? 'Stock transfer item' : 'Stock item with expiry'}
						</Text>
						<Text style={styles.dialogMeta}>
							Product: {item.product?.name || 'Unknown'}
						</Text>
						<Text style={styles.dialogMeta}>
							Required quantity: {expectedQuantity} units
						</Text>
						{sourceIsWarehouse ? (
							<Text style={styles.dialogMeta}>
									Expiry batches are carried forward from the source warehouse and cannot be edited during receipt.
							</Text>
						) : (
							<>
								<Text style={styles.dialogMeta}>
										Current captured summary: {getExpirySummaryText(item.expiryBatches, timezone)}
								</Text>
								<Text style={styles.dialogMeta}>
										New expiry dates must be after today.
								</Text>
								<Text style={[
									styles.dialogMeta,
									expiryDateRequired && styles.requiredPolicyText,
								]}
								>
										Expiry date policy: {getProductExpiryDatePolicyName(expiryDatePolicy)}{expiryDateRequired ? '. Expiry dates must be entered before this product can be stocked.' : ''}
									{expiryDateRecommended ? '. Expiry dates are recommended for this product.' : ''}
								</Text>
							</>
						)}
						{(rows || []).map((row, index) => (
							<View key={`${item.id}_${index}`} style={styles.batchRow}>
								<Text style={styles.batchHeading}>{sourceIsWarehouse ? 'Quantity to stock' : `Batch ${index + 1}`}</Text>
								<StocktakeQuantitySelector
									key={`${item.id}_${index}_${row.quantity || 0}`}
									warehouseProduct={getWarehouseProductForDeliveryItem(item)}
									value={valueExists(row.quantity) ? row.quantity : null}
									onChange={value => updateRow(index, 'quantity', value)}
									disabled={loading}
								/>
								{!sourceIsWarehouse && (
									<>
										<DateSelectorInput
											value={row.expiryDate || ''}
											onChange={value => updateRow(index, 'expiryDate', value)}
											placeholder={expiryDateRequired ? 'Expiry date (required)' : 'Expiry date (optional)'}
											minimumDate={minimumExpiryDate}
											disabled={loading}
											style={styles.batchInput}
										/>
										<TextInput
											value={row.supplierBatchCode || ''}
											onChange={value => updateRow(index, 'supplierBatchCode', value)}
											placeholder="Supplier batch code (optional)"
											style={styles.batchInput}
										/>
										{rows.length > 1 && (
											<Button
												label="Remove batch"
												onPress={() => removeRow(index)}
												disabled={loading}
											/>
										)}
									</>
								)}
							</View>
						))}
						<View style={styles.batchFooter}>
							{!sourceIsWarehouse && (
								<Button
									label="Add batch"
									onPress={addRow}
									disabled={loading}
								/>
							)}
							<Text style={styles.batchTotalText}>
								{sourceIsWarehouse ? 'Quantity' : 'Total'}: {rowsTotal} / {expectedQuantity} units
							</Text>
						</View>
						{canConfirmQuantityChange && (
							<Button
								label="Update delivery quantity"
								onPress={() => onConfirmQuantityChange(rowsTotal)}
								loading={loading}
								disabled={loading}
							/>
						)}
						{Boolean(error) && (
							<Text style={styles.errorText}>{error}</Text>
						)}
					</ScrollView>
					<View style={styles.dialogButtonRow}>
						<Button
							label="Cancel"
							onPress={onClose}
							disabled={loading}
						/>
						<Button
							label="Stock item"
							secondary={true}
							onPress={onSubmit}
							loading={loading}
							disabled={loading || !canSubmit}
						/>
					</View>
				</View>
			)}
		</Dialog>
	);
};

const createStyles = ({ ui: { colors, type, space, radius } }) => ({
	batchDialogContainer: {
		width: '100%',
		maxHeight: '78%',
		minWidth: 320,
		maxWidth: 560,
	},
	batchDialogScroll: {
		flexGrow: 0,
	},
	batchDialogScrollContent: {
		paddingBottom: space.sm,
		paddingRight: space.xxs,
	},
	batchRow: {
		borderWidth: 1,
		borderColor: colors.border,
		borderRadius: radius.card,
		backgroundColor: colors.card,
		padding: space.md,
		marginTop: space.sm,
		gap: space.sm,
	},
	batchHeading: {
		...type.overline,
		color: colors.textSecondary,
	},
	batchInput: {
		marginTop: space.xs,
	},
	batchFooter: {
		marginTop: space.md,
		gap: space.sm,
	},
	batchTotalText: {
		...type.secondary,
		color: colors.textSecondary,
	},
	errorText: {
		...type.bodySmallSemibold,
		marginTop: space.sm,
		color: colors.danger,
	},
	requiredPolicyText: {
		color: colors.warning,
	},
	dialogButtonRow: {
		marginTop: space.md,
		flexDirection: 'row',
		justifyContent: 'space-between',
		gap: space.sm,
	},
	dialogTitle: {
		...type.cardTitle,
		color: colors.text,
		marginBottom: space.sm,
		paddingRight: space.xxl,
	},
	dialogMeta: {
		...type.bodySmall,
		color: colors.textSecondary,
		marginBottom: space.sm,
	},
});

export default ReceiveWithExpiryDialog;
